import { useState, useEffect, useRef, forwardRef, useImperativeHandle, } from 'react';
import router, { useRouter } from 'next/router';
import { Reorder, AnimatePresence } from 'framer-motion';

import { useQuery } from '@tanstack/react-query';
import { defaultColor } from '@components/Presentation/constants';

import useLoadingTimeout from '@lib/hooks/useLoadingTimeout';
import Loader from '@atoms/Atom/Loader';
import Image from '@atoms/Image';

import FantasticFansItem from './FantasticFansItem';

import { getFantasticFansPaginated } from '@services/youtube-platform.service';

import useNotificationTimer from '@lib/hooks/useNotificationTimer ';
import useFonts from '@lib/hooks/useFonts';
import useAnimation from '@lib/hooks/useAnimation';
import useFontSizeCalculator from '@components/Presentation/helpers/fontSizeCalculator';
const FantasticFans = forwardRef(({
  streamId,
  currentRosFeature,
  setShowNotification,
  isSandbox = false,
  isTrackModal = false,
}, ref) => {
  const [fans, setFans] = useState([]);
  const [animatedCount, setAnimatedCount] = useState([]);
  const [animatedRelativePercentage, setAnimatedRelativePercentage] = useState([]);
  const [colors, setColors] = useState(currentRosFeature?.color || defaultColor);
  const [isLoading, setIsLoading] = useLoadingTimeout(30000); ;

  const isFirstCallRef = useRef(true);

  const joinType = useRouter().query?.joinType;

  useNotificationTimer(currentRosFeature, fans, setShowNotification);

  const { getFontStyle } = useFonts();
  const { fontFamily } = getFontStyle(currentRosFeature?.setting?.FontStyle || 'Sans-Serif');

  const calculatedFontSize = useFontSizeCalculator(currentRosFeature?.setting?.ffFontSize ? Number(currentRosFeature?.setting?.ffFontSize) : 1.2, 1.8, 'rem');
  const computedFontSize = isTrackModal ? '20px' : calculatedFontSize;

  const fansData = useQuery({
    queryKey: [
      'fans',
      streamId,
      currentRosFeature?.id,
      currentRosFeature?.setting?.fantasticFansShowWinners,
    ],
    queryFn: () =>
      getFantasticFansPaginated(streamId, {
        filter: currentRosFeature?.setting?.fantasticFansShowWinners,
      }),
    enabled: Boolean(currentRosFeature?.setting?.fantasticFansShowWinners),
    refetchOnWindowFocus: true
  });

  useEffect(() => {
    if (fansData?.data?.status && fansData?.data?.entity?.rows?.length > 0) {
      if (isFirstCallRef.current) {
        setIsLoading(true);
      }

      setFans(fansData.data.entity.rows);

      if (isFirstCallRef.current) {
        setIsLoading(false);
        isFirstCallRef.current = false;
      }
    }
  }, [fansData]);

  useAnimation(fans, 'count', setAnimatedCount, currentRosFeature?.id);
  useAnimation(fans, 'relativePercentage', setAnimatedRelativePercentage, currentRosFeature?.id);

  useEffect(() => {
    setAnimatedCount([]);
    setAnimatedRelativePercentage([]);
  }, [currentRosFeature?.id]);

  useEffect(() => {
    if (currentRosFeature?.color) {
      setColors(currentRosFeature.color);
    }
  }, [currentRosFeature?.color]);

  useEffect(() => {
    if (currentRosFeature?.setting) {
      fansData.refetch();
    }
  }, [currentRosFeature?.setting]);

  const stopCurrentFeature = () => {
    router.push({ pathname: `/streams/${streamId}/ros`, query: joinType && { joinType } });
  };

  useImperativeHandle(ref, () => ({ stopCurrentFeature }));

  const getStyle = () => ({
    cursor: 'default',
    ...(isSandbox && { height: 'calc(100vh - 400px)' }),
  });

  return (
    <div className='flex w-full h-full relative' >
      <div
        id='scrollableDiv'
        style={{ ...getStyle(), fontFamily }}
        className={'flex-1 scrollable-container max-w-6xl mx-auto flex flex-col pb-4 overflow-y-auto!'}
      >
        {isLoading ? <Loader showMessages={false} />
          : <> {fans.length > 0 ?
            <Reorder.Group
              axis="y"
              values={fans.map((fan) => fan.id)}
              onReorder={() => { }}
              className="space-y-2"
              layout
            >
              <AnimatePresence>
                <FantasticFansItem isTrackModal={isTrackModal} fans={fans} animatedCount={animatedCount} animatedRelativePercentage={animatedRelativePercentage} colors={colors} fontSize={computedFontSize} fontFamily={fontFamily} />
              </AnimatePresence>
            </Reorder.Group>
            : <div className='absolute m-0 top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2'>
              <div className='flex flex-col items-center'>
                <Image src='/images/chat.svg' />
                <div className='mt-8'>
                  <div className='flex justify-center items-center'>
                    <span className='font-medium	text-white text-center text-lg leading-6'>Nobody has commented</span><br />
                  </div>
                  <div className='flex justify-center items-center'>
                    <span className='font-medium	text-white text-center text-lg leading-6'>yet in the chat</span>
                  </div>
                </div>
              </div>
            </div>
          }
          </>}
      </div>
    </div>
  );
});

export default FantasticFans;
